/**
 * useCommunalChat — the MINIMAL driver-side caller for
 * `runCommunalDriverSession` (mesh-core's `stageOrchestrator.ts`). Closes
 * the gap `docs/COMMUNAL.md`'s "What's NOT done" section flagged as the
 * single largest remaining piece of M3: nothing in this repo built the
 * `CommunalRoute`/`CommunalRouteFn` a real driver page needs (roster ->
 * `buildCommunalTopology` -> `planCommunalRoute` + `communalAttachOrder` ->
 * the shapes `runCommunalDriverSession` expects) or wired it into
 * something a Playwright test could drive.
 *
 * Deliberately NOT the full OWUI-style chat UI (that's M5/product-
 * integration work) — this hook is the same altitude as `useStagePipeline`
 * (Phase C's legacy driver hook), generalized to the coordinator-free
 * communal path:
 *
 *   1. Host stage 0 LOCALLY, covering `[0, driverLayers)` — every communal
 *      host's own claim is anchored at `driverLayers` (see
 *      `communalAssembly.ts`), so this boundary is a fixed protocol
 *      constant (`STAGE_DRIVER_LAYERS`), never planned per-driver the way
 *      the legacy path's `planPipelineForDriver` adapts to local WebGPU
 *      capacity — a communal driver's local range must match what every
 *      host on the mesh assumes, or `buildCommunalTopology`'s coverage
 *      accounting is comparing incompatible ranges.
 *   2. Tokenize/detokenize locally (same `StageWorkerClient`).
 *   3. Build a `CommunalRoute` from the LIVE roster (`useMeshRoster`'s
 *      `loadedStages` union, via `buildCommunalTopology` + `planCommunalRoute`
 *      + `communalAttachOrder`) and hand it to `runCommunalDriverSession` —
 *      the actual attach/preflight/decode/replan state machine mesh-core
 *      already implements and unit-tests; this hook is JUST the caller.
 *   4. Stream tokens back, detokenizing locally as they arrive.
 *
 * Cross-tab leader election reuses `useStagePipeline`'s exact
 * `acquireLeaderLock` idiom ("one driver session per browser tab") — a
 * second concurrent `start()` call in the SAME tab sees `phase: 'follower'`
 * instead of double-driving the mesh. Multiple TABS (real separate
 * `navigator.locks` managers) each get their own independent communal chat
 * — this is exactly what the M3/M4 acceptance e2e's "2 concurrent driver
 * tabs" scenario exercises.
 *
 * M4 — contribution-economy telemetry (see `docs/ECONOMY.md`'s "Injection
 * story"): when `standingLedger` is supplied, every attached segment's
 * completion or replan-triggering failure feeds
 * `standingLedger.recordService({hostPeerId, layersServed, framesServed,
 * servingMs, sessionCompleted}, now)` for the remote host(s) THIS run
 * actually attached to — the "driver directly witnesses host service" half
 * of the economy. `priorityScore` (typically `bindPriorityScore(ledger,
 * clock)`) is threaded into `planCommunalRoute`/`communalAttachOrder`'s
 * ranking AND `runCommunalDriverSession`'s replan-jitter formula, so a
 * driver with real standing both routes toward AND recovers faster via
 * known-good hosts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { buildCommunalTopology, communalAttachOrder, planCommunalRoute, planThinDriverRoute, runCommunalDriverSession, } from '@unstable-legion/core';
import { StageWorkerClient, warmUpStageWorker } from './stageWorkerClient.js';
import { emitTelemetry } from './meshResilience.js';
import { acquireLeaderLock } from './useStagePipeline.js';
import { detectWebGpuLimits } from './webgpuLimits.js';
import { resolveCommunalShardPlan, OPFS_QUOTA_CEILING_BYTES } from './useCommunalHost.js';
import { STAGE_CTX_SIZE, STAGE_DRIVER_LAYERS, STAGE_MODEL_ID, STAGE_N_EMBD, STAGE_TOTAL_LAYERS, stageShardUrls, } from './stageModelSource.js';
function sameResidentConfig(a, b) {
    return (a.modelId === b.modelId &&
        a.layerStart === b.layerStart &&
        a.layerEnd === b.layerEnd &&
        a.totalLayers === b.totalLayers &&
        a.ctxSize === b.ctxSize &&
        a.wireDtype === b.wireDtype);
}
const DEFAULT_BOOTSTRAP_STEP_MS = 120_000;
const DEFAULT_LOAD_MS = 300_000;
const DEFAULT_SERVE_FIRST_STAGE_MAX_SESSIONS = 1;
/**
 * How long the resident stage-0 worker (see `serveFirstStage`) stays
 * loaded after the toggle goes OFF before its weights are freed — same
 * "a blip shouldn't pay a multi-minute reload" rationale, and the same
 * constant, as `useStageHost.ts`'s `RESIDENT_GRACE_MS`. A true unmount
 * frees immediately and does not wait this out.
 */
const RESIDENT_GRACE_MS = 10 * 60_000;
/**
 * Cap on total pipeline stages a route may use. `Infinity` = the host-side
 * relay is trusted to forward across arbitrarily many hops. Set to `2` as a
 * runtime kill-switch to pin the mesh to the proven driver→one-remote shape
 * if a relay regression ever ships — no code change beyond this line. A
 * topology that needs more hops than this becomes unroutable (the greedy
 * cover still prefers a single host that spans the whole range, so this only
 * bites a genuinely fragmented mesh).
 */
const MAX_ROUTE_STAGES = Infinity;
const DEFAULT_PING_MS = 30_000;
const DEFAULT_LOCK_NAME = 'unstable-legion-communal-chat-driver-leader-v1';
const DEFAULT_MAX_DECODE_TOKENS = 64;
export function useCommunalChat(opts) {
    const { peer, createStageWorker, modelId = STAGE_MODEL_ID, totalLayers = STAGE_TOTAL_LAYERS, driverLayers = STAGE_DRIVER_LAYERS, manifestUrl, localFolderHandle, nEmbd = STAGE_N_EMBD, ctxSize = STAGE_CTX_SIZE, wireDtype = 'i8', defaultMaxDecodeTokens = DEFAULT_MAX_DECODE_TOKENS, leaderLockName = DEFAULT_LOCK_NAME, spreadWidth = 3, priorityScore = () => 0, standingLedger, thinDriver = false, thinTokenizer, textRelay = false, serveFirstStage = false, serveFirstStageMaxSessions = DEFAULT_SERVE_FIRST_STAGE_MAX_SESSIONS, timeouts, queueWaitMs, replanJitterMs, telemetry, log = () => undefined, } = opts;
    const [status, setStatus] = useState({ phase: 'idle' });
    const [plan, setPlan] = useState(undefined);
    const [tokens, setTokens] = useState([]);
    const [text, setText] = useState('');
    const [restartCount, setRestartCount] = useState(0);
    const [readyStageIndexes, setReadyStageIndexes] = useState([]);
    const [loadProgress, setLoadProgress] = useState(undefined);
    const [lastTiming, setLastTiming] = useState(undefined);
    const lockRef = useRef(null);
    const localWorkerRef = useRef(null);
    const sessionRef = useRef(null);
    const runningRef = useRef(false);
    // Latest sendStageFrame byte size per destination peerId — see
    // UseCommunalChatHandle.hopBytes's doc comment. A ref (not state):
    // updated once per outbound stage frame (same frequency as the 'token'
    // event, which already triggers a re-render via setTokens), so there's
    // no need for its own render-triggering state churn — by the time a
    // consumer's render reads it, the correlated token-driven re-render has
    // already happened.
    const hopBytesRef = useRef({});
    // ── REUSE-STAGE0 (serveFirstStage) — the resident stage-0 worker's
    // engine state, hook-lifetime (survives every `start()`/`finally`
    // teardown, unlike `localWorkerRef` above which is scoped to the
    // CURRENT turn) — mirrors `useStageHost.ts`'s `engineRef`/`StageEngine`
    // doc comment: holding this in a ref (not component state) is what lets
    // a re-render never accidentally tear down a worker that took real
    // shard-download time to load. `residentStageZeroRef` is the PUBLIC
    // (readonly-typed) view of the same underlying object a future serving
    // layer adopts — see `UseCommunalChatHandle.residentStageZeroRef`'s doc
    // comment. Both refs are created once and mutated in place, never
    // reassigned, so their identity is stable for the life of the hook. ────
    const residentEngineRef = useRef({ client: null, config: null, loadInFlight: null, serveMaxSessions: 0, loadEpoch: 0 });
    const residentStageZeroRef = useRef(null);
    const disposeResidentTimerRef = useRef(undefined);
    // Same `logRef` discipline `useStageHost.ts` documents at length (see
    // that file's `logRef`/DEBUG-CASEFILE.md note): the grace-timer/unmount
    // effects below must NOT depend on the caller's `log` callback identity
    // (which churns every render whenever `log` is an inline arrow) — doing
    // so would tear down and re-arm the grace timer on every unrelated
    // re-render, and a timer that's perpetually cancelled-and-restarted
    // never actually fires, silently pinning the resident worker's weights
    // in VRAM forever after the toggle goes off.
    const logRef = useRef(log);
    logRef.current = log;
    /** Free the resident stage-0 worker (if any) and clear its public ref.
     * Stable identity (empty deps — reads `log`/engine state via refs) so
     * the grace-timer/unmount effects below never restart because of it. */
    const disposeResidentStageZero = useCallback((reason) => {
        const eng = residentEngineRef.current;
        const client = eng.client;
        eng.client = null;
        eng.config = null;
        eng.loadInFlight = null;
        eng.serveMaxSessions = 0;
        residentStageZeroRef.current = null;
        if (client) {
            logRef.current(`[communal-chat] DISPOSING resident stage-0 worker + terminate() (${reason})`);
            void client.dispose().catch(() => undefined);
        }
    }, []);
    // True unmount (tab/route gone) — free the resident worker immediately,
    // regardless of the idle grace window below. Mirrors
    // `useStageHost.ts`'s identical true-unmount-always-frees effect.
    useEffect(() => {
        return () => disposeResidentStageZero('component unmounted');
    }, [disposeResidentStageZero]);
    // A SUSTAINED `serveFirstStage: false` frees the resident worker's VRAM;
    // a transient toggle-off (or a re-render) must not — same rationale as
    // `useStageHost.ts`'s identical `RESIDENT_GRACE_MS` effect (a cold
    // reload is a multi-minute multi-GB round trip). Re-enabling inside the
    // window cancels the pending free entirely and the worker is reused
    // as-is (see `ensureResidentStageZero` below).
    useEffect(() => {
        if (serveFirstStage) {
            if (disposeResidentTimerRef.current !== undefined) {
                clearTimeout(disposeResidentTimerRef.current);
                disposeResidentTimerRef.current = undefined;
            }
            return;
        }
        disposeResidentTimerRef.current = setTimeout(() => {
            disposeResidentTimerRef.current = undefined;
            disposeResidentStageZero(`serveFirstStage off for ${Math.round(RESIDENT_GRACE_MS / 60_000)}min`);
        }, RESIDENT_GRACE_MS);
        return () => {
            if (disposeResidentTimerRef.current !== undefined) {
                clearTimeout(disposeResidentTimerRef.current);
                disposeResidentTimerRef.current = undefined;
            }
        };
    }, [serveFirstStage, disposeResidentStageZero]);
    /**
     * Ensure the resident stage-0 worker is loaded and matches the CURRENT
     * `serveFirstStage` config (model/layers/ctx/dtype AND the served-lane
     * ceiling) — reuses it as-is when it already matches (the common case:
     * every turn after the first while `serveFirstStage` stays on), awaits
     * an in-flight load from a near-simultaneous call instead of starting a
     * second one, and otherwise (re)loads it exactly the way the non-
     * resident per-turn path always has (same manifest resolution, same
     * `warmUpStageWorker` dispatch) — the only difference is `maxSessions:
     * 1 + serveFirstStageMaxSessions` at load time (see
     * `ResidentStageZero.serveMaxSessions`'s doc comment for the off-by-one)
     * and that the loaded worker is stashed on `residentEngineRef` instead
     * of being torn down in `start()`'s `finally`.
     */
    const ensureResidentStageZero = useCallback(async () => {
        const eng = residentEngineRef.current;
        const want = { modelId, layerStart: 0, layerEnd: driverLayers, totalLayers, ctxSize, wireDtype };
        if (eng.client && eng.config && sameResidentConfig(eng.config, want) && eng.serveMaxSessions === serveFirstStageMaxSessions) {
            return eng.client;
        }
        if (eng.loadInFlight) {
            const client = await eng.loadInFlight.catch(() => null);
            if (client && eng.client && eng.config && sameResidentConfig(eng.config, want) && eng.serveMaxSessions === serveFirstStageMaxSessions) {
                return client;
            }
        }
        const doLoad = (async () => {
            // A stale resident (different config/ceiling than what's wanted NOW)
            // must be freed before loading fresh — same "never leave two
            // worker instances alive" discipline as `useStageHost.ts`'s
            // `ensureWorkerLoaded` -> `disposeWorker` call.
            if (eng.client) {
                const stale = eng.client;
                eng.client = null;
                eng.config = null;
                residentStageZeroRef.current = null;
                await stale.dispose().catch(() => undefined);
            }
            const { plan: stage0Plan } = await resolveCommunalShardPlan({ layerStart: 0, layerEnd: driverLayers, includeOutput: false }, {
                manifestUrl,
                fallbackShardUrls: stageShardUrls,
                includeEmbeddings: true,
                opfsQuotaBytes: OPFS_QUOTA_CEILING_BYTES,
            });
            const client = new StageWorkerClient(createStageWorker(), 'communal-chat-stage-0-resident', logRef.current);
            await client.load({
                modelId,
                layerStart: 0,
                layerEnd: driverLayers,
                totalLayers,
                shardUrls: stage0Plan.shardUrls,
                shardHashes: stage0Plan.shardHashes,
                shardBytes: stage0Plan.shardBytes,
                ctxSize,
                // RESIDENT SERVE — reserve N extra lanes beyond this hook's own
                // fused (no-sessionId) lane, so a future serving layer can adopt
                // this SAME worker for served sessions with no second load. See
                // `ResidentStageZero.serveMaxSessions`'s doc comment.
                maxSessions: 1 + serveFirstStageMaxSessions,
            }, { useMemoryShardStore: stage0Plan.useMemoryShardStore, localFolderHandle });
            logRef.current(`[communal-chat] resident stage-0 loaded (nEmbd=${client.nEmbd}); warming up…`);
            await warmUpStageWorker(client, logRef.current);
            logRef.current('[communal-chat] resident stage-0 warm-up done');
            eng.client = client;
            eng.config = want;
            eng.serveMaxSessions = serveFirstStageMaxSessions;
            eng.loadEpoch += 1;
            residentStageZeroRef.current = {
                client,
                modelId,
                layerStart: 0,
                layerEnd: driverLayers,
                totalLayers,
                ctxSize,
                wireDtype,
                nEmbd: client.nEmbd,
                serveMaxSessions: serveFirstStageMaxSessions,
                epoch: eng.loadEpoch,
            };
            return client;
        })();
        eng.loadInFlight = doLoad;
        try {
            return await doLoad;
        }
        finally {
            if (eng.loadInFlight === doLoad)
                eng.loadInFlight = null;
        }
    }, [modelId, totalLayers, driverLayers, ctxSize, wireDtype, manifestUrl, localFolderHandle, createStageWorker, serveFirstStageMaxSessions]);
    // EAGER SERVE — the instant "Serve the first stage" is on, load the
    // resident stage-0 so a thin client that routes here isn't stuck waiting out
    // the full [0, driverLayers) download on demand, and so this peer isn't
    // advertised as a stage-0 host until the weights are actually resident
    // (residentStageZeroRef → useLocalStageServe's ad only goes non-null after
    // load+warm-up). A transient toggle-off is absorbed by RESIDENT_GRACE_MS.
    //
    // KNOWN FRAGILITY (2026-07-18): this spawns a SECOND wasm worker that
    // requests the WebGPU device while a capable peer's body-host worker may
    // still be loading. On a HEALTHY GPU that's fine (verified live). On a GPU
    // already wedged by repeated OOM/abort cycles (e.g. thrashing the 14B
    // channel, which can't fit — see [[legion-model-channels-slicing]]) the
    // concurrent device request can fail ("state changed since read from disk")
    // and stall the body load. A browser restart clears it. The real hardening
    // is to SERIALIZE this behind the host load rather than race — tracked, not
    // yet done; not worth ripping out a working feature for a stressed-GPU edge.
    useEffect(() => {
        if (!serveFirstStage)
            return;
        let cancelled = false;
        void ensureResidentStageZero().catch((err) => {
            if (!cancelled) {
                logRef.current(`[communal-chat] eager resident stage-0 load failed: ${err instanceof Error ? err.message : String(err)}`);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [serveFirstStage, ensureResidentStageZero]);
    const teardownLocalWorker = useCallback(async () => {
        const w = localWorkerRef.current;
        localWorkerRef.current = null;
        // A RESIDENT stage-0 worker (serveFirstStage on) must survive this
        // turn's teardown — it's freed only by the idle-grace/unmount effects
        // above, never here. Identity comparison (not a separate boolean flag)
        // is the simplest correct test: `w` is the SAME client instance as
        // `residentEngineRef.current.client` iff `start()` adopted the
        // resident worker this turn instead of creating a transient one.
        if (w && w !== residentEngineRef.current.client) {
            await w.dispose().catch(() => undefined);
        }
    }, []);
    const abort = useCallback((reason) => {
        sessionRef.current?.abort(reason ?? 'user abort');
    }, []);
    const start = useCallback(async (prompt, startOpts) => {
        if (runningRef.current)
            return false;
        if (!peer) {
            setStatus({ phase: 'error', error: 'mesh not connected' });
            return false;
        }
        runningRef.current = true;
        setPlan(undefined);
        setTokens([]);
        setText('');
        setRestartCount(0);
        setReadyStageIndexes([]);
        hopBytesRef.current = {};
        setStatus({ phase: 'planning' });
        const lock = await acquireLeaderLock(leaderLockName);
        if (!lock) {
            setStatus({ phase: 'follower' });
            runningRef.current = false;
            return false;
        }
        lockRef.current = lock;
        emitTelemetry(telemetry, { name: 'chat_started', props: { modelId } });
        const t0 = Date.now();
        const elapsed = () => `+${Date.now() - t0}ms`;
        // Decode-speed metric (see ChatGenTiming / the tok/s badge). Spans the
        // WHOLE generation (across any replan) — first generated token to last
        // — so a replan's re-prefill pause honestly shows up as lower tok/s
        // rather than being hidden. `t0` is the session-start anchor for TTFT.
        let genFirstTokenAt = 0;
        let genLastTokenAt = 0;
        let genTokenCount = 0;
        setLastTiming(undefined);
        // textRelay implies thin routing (no local stage 0) — see
        // `textRelay`'s doc comment. A caller sets `textRelay: true` alone;
        // `thinDriver: true` is not required alongside it.
        const effectiveThin = thinDriver || textRelay;
        /** Build (or rebuild, for a replan) a `CommunalRoute` from the LIVE
         * roster — pure read, no I/O, cheap enough to call fresh every time
         * rather than caching (`buildCommunalTopology`'s own doc comment:
         * "cheap to call on every roster change"). */
        function buildRoute(excludePeerIds) {
            const roster = peer.roster.snapshot();
            // Thin/textRelay drivers cover [0, totalLayers) and need a remote
            // isFirst host; capable drivers cover [driverLayers, totalLayers)
            // and host [0, driverLayers) locally. The `communalStart` + planner
            // differ; the attach-order/candidate shape is identical.
            const topology = buildCommunalTopology(roster, { modelId, totalLayers, driverLayers, ...(effectiveThin ? { communalStart: 0 } : {}) }, { excludePeerIds });
            const routePlan = effectiveThin
                ? planThinDriverRoute(topology, { driverPeerId: peer.selfId, priorityScore, spreadWidth, nEmbd, wireDtype, maxStages: MAX_ROUTE_STAGES })
                : planCommunalRoute(topology, { driverPeerId: peer.selfId, priorityScore, spreadWidth, nEmbd, wireDtype, maxStages: MAX_ROUTE_STAGES });
            if (!routePlan)
                return null;
            const attachOrder = communalAttachOrder(topology, { driverPeerId: peer.selfId, priorityScore, spreadWidth });
            return { plan: routePlan, attachOrder };
        }
        try {
            // Capable drivers need usable WebGPU for the local stage 0. A
            // THIN/TEXT-RELAY driver deliberately has none — it hosts no stage
            // and relies on a remote isFirst host (see `docs/OPTIONAL-STAGE0.md`),
            // so the WebGPU gate is skipped. The token-id thin path still needs a
            // CPU-only tokenizer of its own; textRelay needs none at all (the
            // isFirst host tokenizes server-side).
            if (!effectiveThin) {
                const gpu = await detectWebGpuLimits();
                if (!gpu.ok)
                    throw new Error(gpu.reason ?? 'WebGPU unavailable for local stage-0');
            }
            else if (!textRelay && !thinTokenizer) {
                throw new Error('thinDriver mode requires a `thinTokenizer` (CPU-only tokenize/detokenize)');
            }
            const initialRoute = buildRoute([]);
            if (!initialRoute) {
                throw new Error(effectiveThin
                    ? 'no feasible THIN communal route — no remote isFirst host covers [0, X) with embeddings yet (a thin/textRelay driver needs one to host its first stage)'
                    : 'no feasible communal route — mesh coverage has a gap, or no communal hosts have advertised loadedStages yet');
            }
            setPlan(initialRoute.plan);
            setStatus({ phase: 'starting' });
            log(`[communal-chat] ${effectiveThin ? 'THIN ' : ''}route: ${initialRoute.plan.stages.map((s) => `${s.peerId}[${s.layerStart},${s.layerEnd})`).join(' -> ')}`);
            // The local CPU work a driver always does — tokenize/detokenize. In
            // capable mode this is the loaded stage-0 worker; in thin mode it's
            // the injected CPU tokenizer (no GPU stage loaded at all); in
            // textRelay mode there is no local tokenize/detokenize at all.
            let localWorker = null;
            if (!effectiveThin) {
                if (serveFirstStage) {
                    // REUSE-STAGE0 — adopt the RESIDENT worker (loaded once, kept
                    // warm across turns, loaded with room for served lanes) instead
                    // of loading + disposing a fresh one this turn. See
                    // `ensureResidentStageZero`'s doc comment; `teardownLocalWorker`
                    // below already knows not to dispose this worker in `finally`
                    // (identity check against `residentEngineRef.current.client`).
                    log(`[communal-chat] ${elapsed()} serveFirstStage on — adopting resident stage-0 (N=${serveFirstStageMaxSessions} served lane(s))`);
                    localWorker = await ensureResidentStageZero();
                    log(`[communal-chat] ${elapsed()} resident stage-0 adopted (nEmbd=${localWorker.nEmbd})`);
                }
                else {
                    // Resolve stage-0's shards from the SAME per-layer manifest the
                    // communal hosts use (embeddings + layers [0, driverLayers)) — NOT
                    // the monolithic `full.gguf` fallback. This keeps every stage on one
                    // model: a stage-0 loaded from a different artifact than the sharded
                    // package emits wrong-width activations the remote stage rejects
                    // ("activation input payload is N bytes, expected M"). Model-agnostic
                    // — widths/fragments come from the manifest. Falls back to
                    // `stageShardUrls()` only when no manifest is set (the test model).
                    const { plan: stage0Plan } = await resolveCommunalShardPlan({ layerStart: 0, layerEnd: driverLayers, includeOutput: false }, {
                        manifestUrl,
                        fallbackShardUrls: stageShardUrls,
                        includeEmbeddings: true,
                        opfsQuotaBytes: OPFS_QUOTA_CEILING_BYTES,
                    });
                    localWorker = new StageWorkerClient(createStageWorker(), 'communal-chat-stage-0', log);
                    await localWorker.load({
                        modelId,
                        layerStart: 0,
                        layerEnd: driverLayers,
                        totalLayers,
                        shardUrls: stage0Plan.shardUrls,
                        shardHashes: stage0Plan.shardHashes,
                        shardBytes: stage0Plan.shardBytes,
                        ctxSize,
                    }, { useMemoryShardStore: stage0Plan.useMemoryShardStore, localFolderHandle });
                    log(`[communal-chat] ${elapsed()} local stage-0 worker loaded (nEmbd=${localWorker.nEmbd}); warming up…`);
                    await warmUpStageWorker(localWorker, log);
                    log(`[communal-chat] ${elapsed()} local stage-0 warm-up done`);
                }
                localWorkerRef.current = localWorker;
            }
            else if (textRelay) {
                log(`[communal-chat] ${elapsed()} text-relay driver — no local stage, no local tokenizer (nEmbd=${nEmbd})`);
            }
            else {
                log(`[communal-chat] ${elapsed()} thin driver — no local stage, CPU tokenizer only (nEmbd=${thinTokenizer.nEmbd})`);
            }
            // TEXT-RELAY: no tokenizer exists on this device at all — the isFirst
            // host tokenizes the prompt server-side and the isFinal host streams
            // decoded text back (see the 'token' event's `text` handling below).
            // These stubs exist only to satisfy the shape every mode shares; the
            // orchestrator never calls tokenize/detokenize in textRelay mode
            // (see `CommunalDriverSessionOptions.textRelay`'s doc comment).
            const tokenizer = textRelay
                ? {
                    nEmbd,
                    tokenize: async () => {
                        throw new Error('textRelay driver must not tokenize locally');
                    },
                    detokenize: async () => {
                        throw new Error('textRelay driver must not detokenize locally');
                    },
                }
                : thinDriver
                    ? {
                        nEmbd: thinTokenizer.nEmbd,
                        tokenize: (t) => thinTokenizer.tokenize(t, true),
                        detokenize: (toks) => thinTokenizer.detokenize(toks),
                    }
                    : {
                        nEmbd: localWorker.nEmbd,
                        tokenize: (t) => localWorker.tokenize(t, true),
                        detokenize: (toks) => localWorker.detokenize([...toks]),
                    };
            // ── M4 telemetry bookkeeping — per ATTACHED segment (reset on
            // every planCreated/replan), not per whole chat: a replan means a
            // DIFFERENT host served the earlier portion, and that earlier
            // host's own credit/no-credit outcome must be recorded against
            // ITS OWN servingMs/frame count, not folded into the replacement
            // host's tally.
            let segmentStartAt = Date.now();
            let segmentTokenCount = 0;
            let segmentHostLayers = [];
            function recordSegmentTelemetry(sessionCompleted) {
                if (!standingLedger)
                    return;
                const now = Date.now();
                const servingMs = Math.max(0, now - segmentStartAt);
                for (const { peerId, layers } of segmentHostLayers) {
                    standingLedger.recordService({ hostPeerId: peerId, layersServed: layers, framesServed: segmentTokenCount, servingMs, sessionCompleted }, now);
                }
            }
            const replanRoute = (lostPeerId, _graceful) => buildRoute(lostPeerId ? [lostPeerId] : []);
            // Thin logging wrapper — same idiom as `useStagePipeline`'s
            // `loggedPeer` (mesh-core's orchestrator is otherwise a black box
            // for "did the sf frame actually get sent, and to whom").
            const loggedPeer = {
                ...peer,
                sendStageFrame: async (bytes, peers) => {
                    log(`[communal-chat] ${elapsed()} sendStageFrame -> ${JSON.stringify(peers)} (${bytes.byteLength} bytes)`);
                    // Pipeline-handoff UI observability: last frame size per
                    // destination peerId (see UseCommunalChatHandle.hopBytes).
                    // `peers` may be a bare peerId or a list (a route can fan a
                    // single frame out to more than one destination).
                    const targets = peers === undefined ? [] : Array.isArray(peers) ? peers : [peers];
                    for (const targetId of targets) {
                        hopBytesRef.current = { ...hopBytesRef.current, [targetId]: bytes.byteLength };
                    }
                    try {
                        await peer.sendStageFrame(bytes, peers);
                    }
                    catch (err) {
                        log(`[communal-chat] ${elapsed()} sendStageFrame -> ${JSON.stringify(peers)} FAILED: ${err instanceof Error ? err.message : String(err)}`);
                        throw err;
                    }
                },
                sendTool: async (frame, peers) => {
                    log(`[communal-chat] ${elapsed()} sendTool(${frame.kind}) -> ${JSON.stringify(peers)}`);
                    await peer.sendTool(frame, peers);
                },
            };
            const handle = runCommunalDriverSession({
                peer: loggedPeer,
                route: initialRoute,
                modelId,
                prompt,
                maxDecodeTokens: startOpts?.maxDecodeTokens ?? defaultMaxDecodeTokens,
                thinDriver: effectiveThin,
                textRelay,
                localHooks: {
                    nEmbd: tokenizer.nEmbd,
                    tokenize: (t) => tokenizer.tokenize(t),
                    detokenize: (toks) => tokenizer.detokenize(toks),
                    // In thin mode the orchestrator never calls reset/prefill/decode
                    // (it synthesizes zero-activation frames and the remote isFirst
                    // host embeds from the token sideband) — these stubs exist only
                    // to satisfy the `DriverStageHooks` shape and must never run.
                    reset: () => (localWorker ? localWorker.reset() : Promise.resolve()),
                    prefill: async (toks, positions) => {
                        if (!localWorker)
                            throw new Error('thin driver: local prefill must not be called');
                        log(`[communal-chat] ${elapsed()} local prefill start (tokens=${toks.length})`);
                        const res = await localWorker.prefill([...toks], [...positions]);
                        log(`[communal-chat] ${elapsed()} local prefill done`);
                        if (!res.activation)
                            throw new Error('local stage-0 prefill produced no activation');
                        return { activation: new Float32Array(res.activation.payload) };
                    },
                    decode: async (token) => {
                        if (!localWorker)
                            throw new Error('thin driver: local decode must not be called');
                        const res = await localWorker.decode(token);
                        if (!res.activation)
                            throw new Error('local stage-0 decode produced no activation');
                        return { activation: new Float32Array(res.activation.payload) };
                    },
                },
                replanRoute,
                timeouts: {
                    bootstrapStepMs: DEFAULT_BOOTSTRAP_STEP_MS,
                    loadMs: DEFAULT_LOAD_MS,
                    pingMs: DEFAULT_PING_MS,
                    ...timeouts,
                },
                queueWaitMs,
                replanJitterMs,
                priorityScore,
            });
            sessionRef.current = handle;
            setStatus({ phase: 'running' });
            handle.on((ev) => {
                log(`[communal-chat] ${elapsed()} event=${ev.type} ${JSON.stringify(ev).slice(0, 300)}`);
                switch (ev.type) {
                    case 'planCreated': {
                        setPlan(ev.plan);
                        segmentStartAt = Date.now();
                        segmentTokenCount = 0;
                        segmentHostLayers = ev.plan.stages
                            .filter((s) => s.stageIndex > 0)
                            .map((s) => ({ peerId: s.peerId, layers: s.layerEnd - s.layerStart }));
                        break;
                    }
                    case 'replan': {
                        // The PREVIOUS attach's outcome — it triggered a replan, so
                        // by definition it did not complete (a replan-then-success
                        // funnels through 'finished' below the way a normal run
                        // does, never re-entering 'replan').
                        recordSegmentTelemetry(false);
                        emitTelemetry(telemetry, { name: 'chat_replan', props: { restartCount: ev.restartCount } });
                        setPlan(ev.plan);
                        setRestartCount(ev.restartCount);
                        setReadyStageIndexes([]);
                        setLoadProgress(undefined);
                        segmentStartAt = Date.now();
                        segmentTokenCount = 0;
                        segmentHostLayers = ev.plan.stages
                            .filter((s) => s.stageIndex > 0)
                            .map((s) => ({ peerId: s.peerId, layers: s.layerEnd - s.layerStart }));
                        break;
                    }
                    case 'stageReady': {
                        setReadyStageIndexes((prev) => (prev.includes(ev.stageIndex) ? prev : [...prev, ev.stageIndex]));
                        // This stage finished loading — drop its in-flight progress so
                        // the UI doesn't keep showing a load line for an assembled stage.
                        setLoadProgress((prev) => (prev && prev.stageIndex === ev.stageIndex ? undefined : prev));
                        break;
                    }
                    case 'loadProgress': {
                        setLoadProgress({
                            stageIndex: ev.stageIndex,
                            peerId: ev.peerId,
                            shardsFetched: ev.shardsFetched,
                            totalShards: ev.totalShards,
                            bytesFetched: ev.bytesFetched,
                            totalBytes: ev.totalBytes,
                            phase: ev.phase,
                        });
                        break;
                    }
                    case 'token': {
                        segmentTokenCount += 1;
                        const tokAt = Date.now();
                        if (genFirstTokenAt === 0)
                            genFirstTokenAt = tokAt;
                        genLastTokenAt = tokAt;
                        genTokenCount += 1;
                        setLoadProgress(undefined); // assembled + generating — no more load line
                        const { generatedTokens } = handle.tokenHistory();
                        setTokens(generatedTokens);
                        if (textRelay) {
                            // No local tokenizer to detokenize with — accumulate the
                            // incremental TEXT DELTA the isFinal host already streamed
                            // back (see stage.token's `text` field / the orchestrator's
                            // `textRelay` doc comment). `ev.text` can be absent/empty on
                            // a step whose token didn't complete a safely-emittable
                            // UTF-8 chunk (host-side buffering, see
                            // `incrementalTextStream.ts`).
                            if (ev.text)
                                setText((prev) => prev + ev.text);
                        }
                        else {
                            void Promise.resolve(tokenizer.detokenize(generatedTokens))
                                .then(setText)
                                .catch(() => undefined);
                        }
                        break;
                    }
                    case 'aborted': {
                        recordSegmentTelemetry(false);
                        // An involuntary abort (host lost / preflight gave up) is a
                        // failure worth reporting; an explicit user stop is not.
                        if (!/\buser\b/i.test(ev.reason)) {
                            emitTelemetry(telemetry, { name: 'chat_failed', props: { reason: ev.reason } });
                        }
                        setStatus({ phase: 'aborted', reason: ev.reason });
                        break;
                    }
                    case 'finished': {
                        recordSegmentTelemetry(true);
                        if (genFirstTokenAt > 0) {
                            const decodeMs = Math.max(0, genLastTokenAt - genFirstTokenAt);
                            setLastTiming({
                                tokenCount: genTokenCount,
                                decodeMs,
                                tokPerSec: genTokenCount > 1 && decodeMs > 0 ? (genTokenCount - 1) / (decodeMs / 1000) : undefined,
                                ttftMs: Math.max(0, genFirstTokenAt - t0),
                            });
                        }
                        setStatus((prev) => (prev.phase === 'aborted' ? prev : { phase: 'finished' }));
                        break;
                    }
                    default:
                        break;
                }
            });
            await handle.result();
        }
        catch (err) {
            const reason = err instanceof Error ? err.message : String(err);
            emitTelemetry(telemetry, { name: 'chat_failed', props: { reason } });
            setStatus({ phase: 'error', error: reason });
        }
        finally {
            await teardownLocalWorker();
            sessionRef.current = null;
            lockRef.current?.release();
            lockRef.current = null;
            runningRef.current = false;
            setLoadProgress(undefined);
        }
        return true;
    }, [
        peer,
        createStageWorker,
        modelId,
        totalLayers,
        driverLayers,
        manifestUrl,
        localFolderHandle,
        nEmbd,
        ctxSize,
        wireDtype,
        defaultMaxDecodeTokens,
        leaderLockName,
        spreadWidth,
        priorityScore,
        standingLedger,
        thinDriver,
        thinTokenizer,
        textRelay,
        serveFirstStage,
        serveFirstStageMaxSessions,
        ensureResidentStageZero,
        timeouts,
        queueWaitMs,
        replanJitterMs,
        telemetry,
        log,
        teardownLocalWorker,
    ]);
    useEffect(() => {
        return () => {
            sessionRef.current?.abort('component unmounted');
            void teardownLocalWorker();
            lockRef.current?.release();
            lockRef.current = null;
        };
    }, [teardownLocalWorker]);
    const isRunning = useCallback(() => runningRef.current, []);
    return {
        status,
        plan,
        tokens,
        text,
        restartCount,
        readyStageIndexes,
        loadProgress,
        lastTiming,
        hopBytes: hopBytesRef.current,
        start,
        abort,
        isRunning,
        residentStageZeroRef,
    };
}
